import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type BannerTone = "info" | "ok" | "warn" | "err";

interface BannerProps {
  tone: BannerTone;
  icon?: LucideIcon;
  children: ReactNode;
  /** Right-aligned action (e.g. a `Button`). */
  action?: ReactNode;
}

const TONES: Record<BannerTone, string> = {
  info: "bg-ds-info-tint text-ds-accent-ink",
  ok: "bg-ds-ok-tint text-ds-ok",
  warn: "bg-ds-warn-tint text-ds-warn",
  err: "bg-ds-err-tint text-ds-err",
};

/** Inline message strip; `err` is announced as an alert, the others as status. */
export function Banner({ tone, icon: Icon, children, action }: BannerProps) {
  return (
    <div
      role={tone === "err" ? "alert" : "status"}
      data-tone={tone}
      className={cn("mb-3.5 flex flex-wrap items-center gap-3 rounded-ds px-3.5 py-2.5 font-ds text-[13.5px]", TONES[tone])}
    >
      {Icon && <Icon className="h-4 w-4 flex-none" aria-hidden="true" />}
      <div className="min-w-0 flex-1 [&_b]:text-ds-ink">{children}</div>
      {action && <div className="ml-auto">{action}</div>}
    </div>
  );
}
